import React from 'react';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import { Image as ImageIcon } from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';

interface GalleryEmptyStateProps {
  isAuthenticated?: boolean;
  onNavigateCreate?: () => void;
}

interface GalleryLoadingStateProps {
  viewMode?: 'grid' | 'single';
  isDesktop?: boolean;
  infoPanelVisible?: boolean;
}

export function GalleryLoadingState({ viewMode = 'single', isDesktop = true, infoPanelVisible = false }: GalleryLoadingStateProps) {
  const { t } = useTranslation();
  
  // Grid skeleton
  if (viewMode === 'grid') {
    return (
      <div
        className="gallery-grid flex-1 overflow-y-auto p-4 md:p-6"
        style={{
          marginRight: isDesktop && infoPanelVisible ? '300px' : '0',
          transition: 'margin-right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1 md:gap-4 auto-rows-fr">
          {Array.from({ length: 20 }).map((_, idx) => (
            <div
              key={idx}
              className="relative aspect-square rounded-lg overflow-hidden border-2 border-border bg-bg-control animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }
  
  // Single view skeleton
  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-text-secondary">{t('gallery.loading')}</p>
      </div>
    </div>
  );
}

export function GalleryNoImagesState({ isAuthenticated = true, onNavigateCreate }: GalleryEmptyStateProps) {
  const { t } = useTranslation();
  const navigate = useAppNavigate();

  return (
    <div className="h-full flex items-center justify-center bg-bg-main">
      <div className="text-center max-w-md px-4">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/20 flex items-center justify-center">
          <ImageIcon className="w-8 h-8 text-accent" strokeWidth={2} />
        </div>
        <h2 className="text-xl font-semibold mb-2">{t('gallery.noImages')}</h2>
        <p className="text-text-secondary mb-6">
          {isAuthenticated ? t('gallery.noImagesDesc') : t('gallery.noImagesGuestDesc')}
        </p>
        {!isAuthenticated ? (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/signup')}
              className="btn btn-primary btn-md min-w-[160px]"
            >
              {t('auth.registerFree')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/signin')}
              className="btn btn-secondary btn-md min-w-[160px]"
            >
              {t('auth.signIn')}
            </button>
          </div>
        ) : (
          onNavigateCreate && (
            <button type="button" onClick={onNavigateCreate} className="btn btn-primary btn-md">
              {t('gallery.createFirst')}
            </button>
          )
        )}
      </div>
    </div>
  );
}

export function GalleryNotFoundState({ isAuthenticated = true, onNavigateCreate }: GalleryEmptyStateProps) {
  const { t } = useTranslation();
  const navigate = useAppNavigate();

  return (
    <div className="h-full flex items-center justify-center bg-bg-main">
      <div className="text-center max-w-md px-4">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/20 flex items-center justify-center">
          <ImageIcon className="w-8 h-8 text-accent" strokeWidth={2} />
        </div>
        <h2 className="text-xl font-semibold mb-2">{t('gallery.noImages')}</h2>
        <p className="text-text-secondary mb-6">
          {isAuthenticated ? t('gallery.noImagesDesc') : t('gallery.noImagesGuestDesc')}
        </p>
        {!isAuthenticated ? (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/signup')}
              className="btn btn-primary btn-md min-w-[160px]"
            >
              {t('auth.registerFree')}
            </button>
            <button
              type="button"
              onClick={() => navigate('/signin')}
              className="btn btn-secondary btn-md min-w-[160px]"
            >
              {t('auth.signIn')}
            </button>
          </div>
        ) : (
          onNavigateCreate && (
            <button type="button" onClick={onNavigateCreate} className="btn btn-primary btn-md">
              {t('gallery.createFirst')}
            </button>
          )
        )}
      </div>
    </div>
  );
}
